import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { selectDataClinic } from 'connect/selectors';
import { useHookResult } from 'pages/funnel/storage/hooks';
import { selectRequestedAppointment } from 'pages/legacy-checkout/connect/selectors';
import { getS3ImageUrl } from 'utils/s3Image';

type ProviderOrPracticeDetails = {
  // Common properties
  photoUrl: string;
  practiceName: string;
  showProvider: boolean;
  providerName?: string;
} & (
    {
      // If showProvider is true, then providerName is required
      showProvider: true;
      providerName: string;
    }
    | {
    // If showProvider is false, then providerName is not required
      showProvider: false;
      providerName?: never;
    }
);

export const useProviderOrPracticeDetails = (): ProviderOrPracticeDetails => {
  const clinic = useSelector(selectDataClinic) || {};
  const { providerId, anyProviderSelected } = useSelector(selectRequestedAppointment) || {};

  const createResult = useCallback(() => {
    const baseResult = {
      photoUrl: '',
      providerName: undefined, // Always include this in the keys so `useHookResult` dependency array does not
      practiceName: '',
      showProvider: false,
    };

    if (!clinic?.providers?.length || !clinic?.name) {
      return baseResult;
    }

    const { doctor } = clinic.providers?.find((provider: any) => provider.id === providerId) || {};
    const practiceName = clinic.name;

    // If a specific provider is selected, show the provider's photo and name
    if (!anyProviderSelected && doctor) {
      const providerPhotoUrl = getS3ImageUrl(doctor.primaryPhoto) || '';
      return {
        ...baseResult,
        photoUrl: providerPhotoUrl,
        providerName: doctor.name,
        practiceName,
        showProvider: true,
      };
    }

    const clinicPhotoUrl = getS3ImageUrl(clinic.primaryPhoto) || '';
    return {
      ...baseResult,
      photoUrl: clinicPhotoUrl,
      practiceName,
      showProvider: false,
    };
  }, [clinic?.providers, clinic?.name, clinic?.primaryPhoto, providerId, anyProviderSelected]);

  return useHookResult(createResult());
};
